import {
  Autocomplete,
  FormControlLabel,
  TextField,
  InputAdornment,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Typography,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import ModalControlProps from "models/explorer/modal-control-props";
import { clamp } from "lodash";
import ExplorerService from "services/ExplorerService";
import { Toaster } from "ui-widgets";

const { explorerService } = new ExplorerService();

const CommonSearchInputs = ({
  searchFeatures,
  handleSearchFeatures,
  customSearch,
  handleCustomSearch,
}: ModalControlProps) => {
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [feature, setFeature] = useState("intersecting");
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController>();

  if (
    feature !== "intersecting" &&
    customSearch.enabled &&
    (((customSearch.type === "google" || customSearch.type === "marker") &&
      customSearch.radius === 0) ||
      customSearch.type === "polyline")
  ) {
    setFeature("intersecting");
  }

  useEffect(() => {
    if (inputValue) {
      setLoading(true);
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      const signal = controller.signal;
      abortRef.current = controller;
      explorerService
        .tagSearch(inputValue, 50, signal)
        .then((res) => {
          res ? setTagOptions(res) : setTagOptions([]);
        })
        .catch((err) => {
          if (err.name !== "AbortError") {
            console.error(err);
            Toaster.makeToast(
              err?.message
                ? err.message
                : "An error occurred when searching boundary or point",
              "error",
            );
          }
        })
        .finally(() => setLoading(false));
    } else {
      setTagOptions([]);
    }
  }, [inputValue]);

  return (
    <>
      <TextField
        fullWidth
        sx={{ my: 1 }}
        disabled={
          !(
            customSearch.enabled &&
            (customSearch.type === "marker" || customSearch.type === "google")
          )
        }
        type="number"
        label="Radius"
        id="radius-textfield"
        inputProps={{ min: 0, max: 5000 }}
        InputProps={{
          endAdornment: <InputAdornment position="end">km</InputAdornment>,
          inputProps: { min: 0, max: 5000 },
        }}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          !Number.isNaN(clamp(parseFloat(event.target.value), 1, 5000) * 1000)
            ? handleCustomSearch({
                ...customSearch,
                radius: clamp(parseFloat(event.target.value), 1, 5000) * 1000,
              })
            : handleCustomSearch({ ...customSearch, radius: 1000 });
        }}
        value={(customSearch.radius / 1000).toString()}
      />
      <FormControl
        fullWidth
        sx={{ my: 1 }}
        disabled={customSearch.type === "none"}
      >
        <FormLabel
          htmlFor="search-feature-select"
          id="search-feature-select-label"
        >
          Search feature
        </FormLabel>
        <RadioGroup
          aria-labelledby="search-feature-select-label"
          value={
            customSearch.type !== "polygon" &&
            customSearch.type !== "rectangle" &&
            customSearch.type !== "system-boundary"
              ? "intersecting"
              : searchFeatures.containedOrIntersecting
          }
          id="search-feature-select"
          onChange={(event) =>
            handleSearchFeatures({
              ...searchFeatures,
              containedOrIntersecting: event.target.value as
                | "intersecting"
                | "contains",
            })
          }
        >
          <FormControlLabel
            value="intersecting"
            control={<Radio />}
            label="Intersecting or contained within"
          />
          <Tooltip
            title={
              'Can only use "Contained" when searching using a system boundary or drawing tools of polygon or rectangle'
            }
          >
            <FormControlLabel
              value="contains"
              disabled={
                customSearch.type !== "polygon" &&
                customSearch.type !== "rectangle" &&
                customSearch.type !== "system-boundary"
              }
              control={<Radio />}
              label="Contained*"
            />
          </Tooltip>
        </RadioGroup>
      </FormControl>

      <FormControl fullWidth sx={{ my: 1 }}>
        <Tooltip title="If you add multiple tags only only features tagged with all the selected terms will be shown">
          <FormLabel htmlFor="tags-explore" id="tags-select-label">
            Limit returned features by tags
          </FormLabel>
        </Tooltip>
        <Autocomplete
          multiple
          aria-labelledby="tags-select-label"
          id="tags-explore"
          options={tagOptions.sort((a, b) => {
            if (a < b) {
              return -1;
            }
            if (a > b) {
              return 1;
            }
            return 0;
          })}
          renderInput={(params) => (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
          fullWidth
          sx={{ my: 1 }}
          onChange={(event, newTags) =>
            handleSearchFeatures({
              ...searchFeatures,
              tags: newTags.map((tag) => tag),
            })
          }
          onInputChange={(e, newInputValue) => setInputValue(newInputValue)}
          renderOption={(props, option) => (
            <li {...props} key={`tag-${option}`}>
              {option}
            </li>
          )}
          noOptionsText={
            !inputValue ? "Start typing to search" : "No options available"
          }
          isOptionEqualToValue={(option, value) => option === value}
          getOptionLabel={(option) => option}
        />
        {searchFeatures.tags && searchFeatures.tags.length > 1 ? (
          <Typography sx={(theme) => ({ color: theme.palette.info.main })}>
            Will only show features with all tags
          </Typography>
        ) : (
          <></>
        )}
      </FormControl>
    </>
  );
};

export default CommonSearchInputs;
